import React, { useState, useMemo } from "react";
import { ColDef, ICellRendererParams } from "ag-grid-community";
import { Flex, Text, Button, TextInput, Grid } from "@jsq/ds/core";
import { Icon } from "@jsq/ds/icons";
import { CustomField, CustomFieldType, getFieldTypeLabel } from "../../types";
import * as styles from "../styles_vanilla.css";
import * as localStyles from "./styles_vanilla.css";

// Type for AG Grid cell renderer parameters with CustomField data
type CustomFieldCellRendererParams = ICellRendererParams<CustomField>;

interface Props {
  onDeleteField: (field: CustomField) => void;
  onEditField: (field: CustomField) => void;
  fields: CustomField[];
}

const CustomFieldsList: React.FC<Props> = ({ onDeleteField, onEditField, fields }) => {
  const [selectedFields, setSelectedFields] = useState<CustomField[]>([]);
  const [searchTerm, setSearchTerm] = useState("");

  // Filter and search functionality
  const filteredFields = useMemo(() => {
    if (!fields) {
      return [];
    }

    const filtered = fields.filter((field) => {
      // Search filter
      const matchesSearch =
        searchTerm === "" || field.name.toLowerCase().includes(searchTerm.toLowerCase());

      return matchesSearch;
    });

    return filtered;
  }, [fields, searchTerm]);

  const columnDefs: ColDef<CustomField>[] = [
    {
      field: "name",
      headerName: "Name",
      flex: 3,
      minWidth: 200,
      cellStyle: { display: "flex", alignItems: "center", height: "100%" },
      cellRenderer: (params: CustomFieldCellRendererParams) => (
        <Text variant="regular-md" color="neutral-800">
          {params.value}
        </Text>
      ),
    },
    {
      field: "type",
      headerName: "Type",
      flex: 2,
      minWidth: 150,
      cellStyle: { display: "flex", alignItems: "center", height: "100%" },
      cellRenderer: (params: CustomFieldCellRendererParams) => {
        const fieldType = params.value;
        if (!fieldType || typeof fieldType !== "string") {
          return (
            <Text variant="regular-md" color="neutral-700">
              Unknown
            </Text>
          );
        }
        return (
          <Text variant="regular-md" color="neutral-700">
            {getFieldTypeLabel(fieldType as CustomFieldType)}
          </Text>
        );
      },
    },
    {
      headerName: "",
      width: 120,
      sortable: false,
      cellStyle: {
        display: "flex",
        alignItems: "center",
        justifyContent: "flex-end",
        height: "100%",
        gap: "8px",
      },
      cellRenderer: (params: CustomFieldCellRendererParams) => {
        if (!params.data) return null;
        const fieldData = params.data;

        return (
          <Flex gap="8px" alignItems="center">
            <Button
              variant="neutral"
              icon="edit"
              onClick={() => {
                onEditField(fieldData);
              }}
              data-testid={`edit-field-${fieldData.id}`}
              aria-label="Edit field"
              className={styles.editButton}
            />
            <Button
              variant="danger"
              icon="trash"
              onClick={(e) => {
                e.stopPropagation();
                onDeleteField(fieldData);
              }}
              data-testid={`delete-field-${fieldData.id}`}
              aria-label="Delete field"
              className={styles.deleteButton}
            />
          </Flex>
        );
      },
    },
  ];

  if (!fields || fields.length === 0) {
    return (
      <div className={localStyles.customFieldsList} data-testid="custom-fields-list">
        <Flex
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          className={styles.emptyStateContainer}
        >
          <Icon type="file" size="x-large" className={styles.emptyStateIcon} />
          <Text variant="regular-xl" className={styles.emptyStateTitle}>
            No fields in this section
          </Text>
          <Text variant="regular-md" color="neutral-600">
            Fields can be moved to this section or new fields can be created
          </Text>
        </Flex>
      </div>
    );
  }

  return (
    <div className={localStyles.customFieldsList} data-testid="custom-fields-list">
      {/* Search Bar */}
      <Flex gap="12px" alignItems="center" className={styles.searchBar}>
        <TextInput
          placeholder="Search fields..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          icon="search"
          className={styles.searchInput}
        />
      </Flex>

      {/* Results Summary */}
      <Flex
        justifyContent="space-between"
        alignItems="center"
        className={styles.resultsSummary}
      >
        <Text variant="regular-sm" color="neutral-600">
          {filteredFields.length} field{filteredFields.length !== 1 ? "s" : ""}
        </Text>
      </Flex>

      <Grid<CustomField>
        columnDefs={columnDefs}
        rowData={filteredFields}
        getRowId={(params) => params.data.id}
        domLayout="autoHeight"
        pagination
        paginationPageSize={20}
        rowSelection="multiple"
        suppressRowClickSelection
        rowHeight={60}
        headerHeight={48}
        onSelectionChanged={(event) => {
          setSelectedFields(event.api.getSelectedRows());
        }}
        data-testid="custom-fields-grid"
        className={localStyles.gridWrapper}
      />

      {selectedFields.length > 0 && (
        <Flex
          justifyContent="space-between"
          alignItems="center"
          className={styles.selectionBarContainer}
        >
          <Text variant="regular-sm" color="neutral-600">
            {selectedFields.length} field
            {selectedFields.length !== 1 ? "s" : ""} selected
          </Text>
          <Button
            variant="primary"
            onClick={() => {
              // Handle bulk actions if needed
              setSelectedFields([]);
            }}
          >
            Clear selection
          </Button>
        </Flex>
      )}
    </div>
  );
};

export default CustomFieldsList;
